import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Icon,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PearlLogoLoader from "components/PearlLogoLoader";
import { useAuth } from "auth/Auth";

/**
 * Fetches /travel-orders/{orderId}/itinerary-pdf as a blob (so the request
 * carries auth headers via axiosInstance) and previews it inline.
 *
 * Usage:
 *   <ItineraryPreviewDialog
 *     open={pdfPreviewOpen}
 *     onClose={() => setPdfPreviewOpen(false)}
 *     orderId={order.id}
 *     orderLabel={order.order_id}
 *   />
 */
function ItineraryPreviewDialog({ open, onClose, orderId, orderLabel }) {
    const { axiosInstance } = useAuth();
    const [blobUrl, setBlobUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !orderId) return undefined;

        let cancelled = false;
        let localUrl = null;

        const load = async () => {
            setIsLoading(true);
            setError("");
            setBlobUrl(null);
            try {
                const response = await axiosInstance.get(
                    `/travel-orders/${orderId}/itinerary-pdf`,
                    { responseType: "blob" }
                );
                if (cancelled) return;
                localUrl = URL.createObjectURL(response.data);
                setBlobUrl(localUrl);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.status === 404
                            ? "No itinerary is available for this booking yet."
                            : "Failed to load the itinerary PDF. Please try again."
                    );
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
            if (localUrl) URL.revokeObjectURL(localUrl);
        };
    }, [open, orderId, axiosInstance]);

    const handleDownload = () => {
        if (!blobUrl) return;
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `itinerary_${orderLabel || orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleOpenNewTab = () => {
        if (blobUrl) window.open(blobUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "#F4F6F9",
                    borderBottom: "1px solid #E8EDF5",
                    py: 1.5,
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 20, color: "#5C6B82" }}>summarize</Icon>
                    <MDTypography variant="button" fontWeight="bold" color="dark">
                        Itinerary Preview{orderLabel ? ` - ${orderLabel}` : ""}
                    </MDTypography>
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={0.5}>
                    {blobUrl && (
                        <>
                            <IconButton size="small" onClick={handleOpenNewTab} title="Open in new tab">
                                <OpenInNewIcon sx={{ fontSize: 18, color: "#5C6B82" }} />
                            </IconButton>
                            <IconButton size="small" onClick={handleDownload} title="Download">
                                <DownloadIcon sx={{ fontSize: 18, color: "#5C6B82" }} />
                            </IconButton>
                        </>
                    )}
                    <IconButton size="small" onClick={onClose} title="Close">
                        <CloseIcon sx={{ fontSize: 18, color: "#5C6B82" }} />
                    </IconButton>
                </MDBox>
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: "#EFF2F6", minHeight: "70vh" }}>
                {isLoading && (
                    <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                        sx={{ height: "70vh" }}
                    >
                        <PearlLogoLoader size="md" message="Loading itinerary..." />
                    </MDBox>
                )}

                {!isLoading && error && (
                    <MDBox p={3}>
                        <Alert severity="error">{error}</Alert>
                    </MDBox>
                )}

                {!isLoading && !error && blobUrl && (
                    <>
                        {/* Inline preview — works in most desktop browsers. */}
                        <embed
                            src={blobUrl}
                            type="application/pdf"
                            style={{ width: "100%", height: "75vh", border: "none" }}
                        />
                        {/* Fallback for browsers (mostly mobile Safari) that ignore <embed> for PDFs. */}
                        <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            py={2}
                            sx={{ borderTop: "1px solid #E8EDF5", bgcolor: "#fff" }}
                        >
                            <MDTypography variant="caption" color="text">
                                Not seeing the preview?
                            </MDTypography>
                            <MDButton variant="outlined" color="info" size="small" onClick={handleOpenNewTab}>
                                Open in new tab
                            </MDButton>
                            <MDButton variant="gradient" color="info" size="small" onClick={handleDownload}>
                                Download PDF
                            </MDButton>
                        </MDBox>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

ItineraryPreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    orderLabel: PropTypes.string,
};

ItineraryPreviewDialog.defaultProps = {
    orderLabel: "",
};

export default ItineraryPreviewDialog;